import { computed, Ref } from 'vue'
import { CustomFieldsSchema, UiSchemaGroup } from '@/models/types/custom-fields-schema'
import Task from '@/models/task.js'
import type { JobFilterCustomDataProperty, MultiselectOption } from '@ironsight/types'
import {
  ButtonFieldDefinition,
  extractFilterableFields,
  FilterableField,
} from '@/utils/field-schema-helper'
import { union, upperFirst } from 'lodash'

export type CustomDataFilterMenuNode =
  | {
      kind: 'group'
      key: string
      label: string
      icon?: string
      children: CustomDataFilterMenuNode[]
    }
  | {
      kind: 'field'
      key: string
      label: string
      icon?: string
      property: JobFilterCustomDataProperty
    }

const SCHEMA_LOOKUP_ENTITY_TO_FILTER_TYPE = {
  user: 'StringLookup',
  location: 'IntLookup',
  business_unit: 'StringLookup',
  company: 'IntLookup',
}

const CUSTOM_DATA_TYPE_TO_FILTER_TYPE = {
  string: 'Text',
  boolean: 'Boolean',
  number: 'Double',
  integer: 'Integer',
}

const CUSTOM_DATA_TYPE_TO_ICON = {
  boolean: 'radio_button_checked',
  string: 'short_text',
  number: 'numbers',
  integer: 'numbers',
}

const CUSTOM_DATA_TYPE_TO_SUPPORTED_COMPARISONS = {
  string: ['Equals', 'Contains'],
  boolean: ['Equals'],
  number: ['Equals', 'GreaterThan', 'LessThan'],
  integer: ['Equals', 'GreaterThan', 'LessThan'],
}

const buildCompositeKey = (key: string, type: string, title: string) =>
  key + '-' + type + '-' + title.replace(/[^0-9a-zA-Z\s]+/g, '').replace(/\s/g, '_')

export function useFilterableCustomDataProperties(
  getTasks: Ref<Task[]>,
  getJobSchemas: Ref<CustomFieldsSchema>,
) {
  const resolvedCustomDataFields = computed(() => {
    const tasksWithSchemas: Task[] = Object.values(getTasks.value).filter(
      (task: Task) => !!task.jobCustomFieldsSchema?.schema,
    )
    if (tasksWithSchemas.length === 0) {
      return []
    }

    const definitions = getJobSchemas.value?.schema?.definitions ?? {}
    const fieldKeyToEnumMap = new Map<string, string[]>()
    const fieldKeyToUiOptions = new Map<string, string[]>()
    const fieldKeyToTaskIdMap = new Map<string, number[]>()
    const allFields = new Map<string, FilterableField>()

    for (const task of tasksWithSchemas) {
      const schema = task.jobCustomFieldsSchema
      schema.uiSchema ??= {}

      const extractedFields = extractFilterableFields(
        schema.schema,
        schema.uiSchema,
        definitions,
        true,
      )

      for (const field of extractedFields) {
        const title = field.definition?.title

        if (!title) continue
        if (!field.definition.type && field.definition.format) continue

        const mapKey = buildCompositeKey(field.pathKey, field.definition.type, field.label)

        const existingTaskId = fieldKeyToTaskIdMap.get(mapKey) ?? []
        fieldKeyToTaskIdMap.set(mapKey, union(existingTaskId, [task.id]))

        if (!task.isActive) continue

        if (field.definition.enum) {
          const existing = fieldKeyToEnumMap.get(mapKey) ?? []
          fieldKeyToEnumMap.set(mapKey, union(existing, field.definition.enum as string[]))
        }

        if (field.uiSchema) {
          const uiOptions = field.uiSchema['ui:options']
          if (uiOptions?.choices) {
            const existing = fieldKeyToUiOptions.get(mapKey) ?? []
            fieldKeyToUiOptions.set(mapKey, union(existing, uiOptions.choices as string[]))
          }

          if (uiOptions?.groups) {
            const groupedItems = uiOptions.groups.flatMap(
              (group: UiSchemaGroup) => group.items ?? [],
            )
            if (groupedItems.length > 0) {
              const existing = fieldKeyToUiOptions.get(mapKey) ?? []
              fieldKeyToUiOptions.set(mapKey, union(existing, groupedItems as string[]))
            }
          }
        }

        if (!allFields.has(mapKey)) {
          allFields.set(mapKey, field)
        }
      }
    }

    return [...allFields.values()]
      .sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()))
      .map((field) => {
        const property = buildCustomDataProperty(
          field,
          fieldKeyToEnumMap,
          fieldKeyToUiOptions,
          fieldKeyToTaskIdMap,
        )

        return {
          field,
          property,
        }
      })
  })

  const simpleCustomDataProperties = computed<JobFilterCustomDataProperty[]>(() =>
    resolvedCustomDataFields.value.map((entry) => entry.property),
  )

  const customDataFilterMenuTree = computed<CustomDataFilterMenuNode[]>(() =>
    buildCustomDataFilterMenuTree(resolvedCustomDataFields.value),
  )

  return {
    simpleCustomDataProperties,
    customDataFilterMenuTree,
  }
}

function buildCustomDataProperty(
  field: FilterableField,
  fieldKeyToEnumMap: Map<string, string[]>,
  fieldKeyToUiOptions: Map<string, string[]>,
  fieldKeyToTaskIdMap: Map<string, number[]>,
): JobFilterCustomDataProperty {
  const isLookupField = Boolean(
    field.definition.lookup?.entity && field.definition.lookup.entity.length > 1,
  )

  const isCreatableSelector = Boolean(
    field.uiSchema?.['ui:widget'] === 'creatableSelector' &&
    field.uiSchema?.['ui:options']?.choices,
  )
  const isGroupedDropdown = Boolean(
    field.uiSchema?.['ui:widget'] === 'genericSelector' && field.uiSchema?.['ui:options']?.groups,
  )

  const isDefinedOptionField = Boolean(
    isCreatableSelector || isGroupedDropdown || field.definition.enum || field.definition.buttons,
  )

  const mapKey = buildCompositeKey(field.pathKey, field.definition.type, field.label)

  const filterType = resolveFilterType(field, isLookupField, isDefinedOptionField)
  const definedOptions = resolveDefinedOptions(
    field,
    mapKey,
    fieldKeyToEnumMap,
    fieldKeyToUiOptions,
    isCreatableSelector,
    isGroupedDropdown,
    isDefinedOptionField,
  )

  return {
    name: 'CustomData',
    label: field.label,
    filterType,
    icon:
      isLookupField || isDefinedOptionField
        ? 'radio_button_checked'
        : CUSTOM_DATA_TYPE_TO_ICON[field.definition.type],
    supportedComparisons:
      isLookupField || isDefinedOptionField
        ? ['In']
        : !field.isNested
          ? (CUSTOM_DATA_TYPE_TO_SUPPORTED_COMPARISONS[field.definition.type] ?? [])
          : getNestedSupportedComparisons(field.definition.type),
    key: buildCompositeKey(field.pathKey, filterType, field.label),
    customDataKey: field.pathKey,
    taskIds: fieldKeyToTaskIdMap.get(mapKey)!,
    lookupEntity: upperFirst(field.definition.lookup?.entity),
    definedOptions,
    canBeEmpty: !field.required,
    sortable: !field.isNested && (field.sortable ?? false),
  }
}

function resolveFilterType(
  field: FilterableField,
  isLookupField: boolean,
  isDefinedOptionField: boolean,
) {
  if (isDefinedOptionField) {
    return 'StringLookup'
  }

  if (isLookupField) {
    return SCHEMA_LOOKUP_ENTITY_TO_FILTER_TYPE[field.definition.lookup?.entity ?? '']
  }

  return CUSTOM_DATA_TYPE_TO_FILTER_TYPE[field.definition.type]
}

function resolveDefinedOptions(
  field: FilterableField,
  mapKey: string,
  fieldKeyToEnumMap: Map<string, string[]>,
  fieldKeyToUiOptions: Map<string, string[]>,
  isCreatableSelector: boolean,
  isGroupedDropdown: boolean,
  isDefinedOptionField: boolean,
): MultiselectOption[] {
  if (!isDefinedOptionField) {
    return []
  }

  if (field.definition.enum && field.definition.type) {
    return (fieldKeyToEnumMap.get(mapKey) ?? []).map((option) => ({
      label: option,
      value: option,
    }))
  }

  if (field.definition.buttons) {
    return field.definition.buttons.map((button: ButtonFieldDefinition) => ({
      label: button.label,
      value: button.value,
    }))
  }

  if (isCreatableSelector) {
    const currentOptions = fieldKeyToUiOptions.get(mapKey)
    if (currentOptions) {
      return buildSortedOptions(currentOptions)
    }

    return (
      field.uiSchema?.['ui:options']?.choices?.map((option: string) => ({
        label: option,
        value: option,
      })) || []
    )
  }

  if (isGroupedDropdown) {
    const currentOptions = fieldKeyToUiOptions.get(mapKey)
    if (currentOptions) {
      return buildSortedOptions(currentOptions)
    }

    return (
      field.uiSchema?.['ui:options']?.groups?.flatMap(
        (group: UiSchemaGroup) =>
          group.items?.map((item: string) => ({
            label: item,
            value: item,
          })) || [],
      ) || []
    )
  }

  return []
}

function buildSortedOptions(options: string[]): MultiselectOption[] {
  return options
    .map((option: string) => ({
      label: option,
      value: option,
    }))
    .sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()))
}

function buildCustomDataFilterMenuTree(
  resolvedFields: Array<{
    field: FilterableField
    property: JobFilterCustomDataProperty
  }>,
): CustomDataFilterMenuNode[] {
  const rootNodes: CustomDataFilterMenuNode[] = []

  for (const { field, property } of resolvedFields) {
    const nonWildcardPath = field.path.filter((segment) => segment !== '*')
    const parentPath = nonWildcardPath.slice(0, -1)
    const parentLabels = field.labelPath.slice(0, -1)

    let currentLevel = rootNodes

    for (let index = 0; index < parentPath.length; index++) {
      const groupKey = parentPath.slice(0, index + 1).join('/')
      const groupLabel = parentLabels[index]

      let existingGroup = currentLevel.find(
        (node): node is Extract<CustomDataFilterMenuNode, { kind: 'group' }> =>
          node.kind === 'group' && node.key === groupKey,
      )

      if (!existingGroup) {
        existingGroup = {
          kind: 'group',
          key: groupKey,
          label: groupLabel,
          icon: index === 0 ? 'folder_open' : 'subdirectory_arrow_right',
          children: [],
        }
        currentLevel.push(existingGroup)
      }

      currentLevel = existingGroup.children
    }

    currentLevel.push({
      kind: 'field',
      key: property.key,
      label: field.labelPath[field.labelPath.length - 1] ?? property.label,
      icon: property.icon,
      property,
    })
  }

  sortCustomDataMenuNodes(rootNodes)
  return rootNodes
}

function sortCustomDataMenuNodes(nodes: CustomDataFilterMenuNode[]) {
  nodes.sort((a, b) => {
    if (a.kind !== b.kind) {
      return a.kind === 'group' ? -1 : 1
    }

    return a.label.toLowerCase().localeCompare(b.label.toLowerCase())
  })

  nodes.forEach((node) => {
    if (node.kind === 'group') {
      sortCustomDataMenuNodes(node.children)
    }
  })
}

function getNestedSupportedComparisons(type: string) {
  switch (type) {
    case 'string':
      return ['Equals', 'Contains']
    case 'boolean':
      return ['Equals']
    // WARNING - Allowing greater/less than here is NOT possible without significant
    // changes and performance implications in the filter handler
    case 'number':
    case 'integer':
      return ['Equals']
    default:
      return []
  }
}
